// src/encounter/lib/sign-action-state.ts
//
// The sign action's state — the QA-verified WrapUpSignLock gates, 1:1 — shared by the Wrap-up quill (OFF)
// and frame v2's labelled "Sign note" button in the note header (6 Oct audit FV2-05), so the two can never
// disagree. Moved verbatim out of wrap-up-actions-header.tsx.
import type { WrapUpSignLockProps } from '../wrap-up-actions-header';
import { signLockBanner } from './to-sign-lock';

export function signActionState(
  signLock: WrapUpSignLockProps | undefined,
  /** The sign action's own words in the tooltip. Frame v2's note header passes 'Sign note', the header
   *  button's label (PROD-3031 nit); the switch-OFF Wrap-up keeps Flutter's 'Sign Note' (the default). */
  label = 'Sign Note',
): { disabled: boolean; title: string } {
  const locked = signLock?.state === 'locked';
  const doctorBlocked = !!signLock && !signLock.isNurse && signLock.canDoctorSign === false;
  // Role-neutral: the sign would queue behind an in-flight A&P save (PROD-1690) — gates nurses too.
  const saveBlocked = !!signLock?.saveInFlight;
  // PROD-3142 (D-78): role-neutral — a refusal the page names (a Fall / Pain list changed elsewhere) blocks every sign.
  const pageBlocked = !!signLock?.blockedReason;
  const signDisabled =
    !signLock || locked || !!signLock.signing || !!signLock.loadError || pageBlocked || doctorBlocked || saveBlocked;
  // ONE label for both roles, and it is Flutter's: `S.signNote` = "Sign Note"
  // (intl_en.arb:303). Andrew, 2026-08-27: "do as flutter in logic and naming."
  //
  // This drops a role distinction the label used to carry — nurse "Sign" vs doctor "Sign & Lock" —
  // which was a REAL difference in what the write does (`toSignLockRequest` sets `lock = !isNurse`,
  // and Flutter routes the nurse through `UpdateSignByNurseEvent` with no lock at all). Flutter
  // still shows one label for both, so the distinction now lives only in the logic and in the
  // tooltip below, not in the button text. Recorded because it is a knowing trade: Flutter's wording
  // over a self-describing label.
  //
  // The note drawer's footer already reads "Sign Note" (signLockLabel on the DS NoteFooter); this
  // was the second of the two sign entry points and the two disagreed until now.
  const signBase = label;
  const signTitle = !signLock
    ? label
    : locked
      ? `${signLockBanner(signLock.state).label} — the clinical record is final`
      : signLock.signing
        ? 'Signing…'
        : signLock.loadError
          ? "Couldn't load the current sign status — reload before signing."
          : pageBlocked
            ? (signLock.blockedReason as string)
          : doctorBlocked
            ? (signLock.signDisabledReason ?? 'Add a diagnosis before signing the encounter.')
            : saveBlocked
              ? 'Saving the assessment — one moment…'
              : signLock.state === 'nurse-signed' && !signLock.isNurse
                ? `A nurse has signed — ${signBase} finalizes and locks the record`
                : signBase;

  return { disabled: signDisabled, title: signTitle };
}

